// src/utils/api.js
import axios from 'axios';
import { getToken, handleLogout } from '../utils/auth';
import { showSnackbar } from '../context/SnackbarContext';

// Create an axios instance with base config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL, // replace with your API base URL
  headers: {
    'Content-Type': 'application/json'
  }
});

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers['Authorization'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (res) => {
    // console.log('intercep', res);
    return res;
  },
  (error) => {
    // console.log("response interceptor error", error);
    let message = '';
    if (error?.response?.data?.message) {
      message = error?.response?.data?.message;
    } else if (error?.message) {
      message = error?.message;
    } else {
      message = 'Something went wrong, please try again later';
    }
    showSnackbar(message, 'error');
    if (error?.status === 401) {
      setTimeout(() => {
        handleLogout();
      }, 500);
    }
  }
);

// Reusable GET request
export const getData = async (endpoint, params = {}) => {
  try {
    const response = await api.get(endpoint, { params });
    return response?.data;
  } catch (error) {
    console.error('GET request error:', error);
    throw error;
  }
};

// Reusable POST request
export const postData = async (endpoint, data) => {
  try {
    const response = await api.post(endpoint, data);
    return response?.data;
  } catch (error) {
    console.error('POST request error:', error);
    throw error;
  }
};

/**
 * PUT helper
 * @param {string} endpoint
 * @param {any} data
 * @param {object} config
 */
export const putData = async (endpoint, data, config = {}) => {
  const response = await api.put(endpoint, data, config);
  return response?.data;
};

/**
 * PATCH helper
 * @param {string} endpoint
 * @param {any} data
 * @param {object} config
 */
export const patchData = async (endpoint, data, config = {}) => {
  const response = await api.patch(endpoint, data, config);
  return response?.data;
};

/**
 * DELETE helper
 * Note: axios.delete can send a body via `config.data`
 * @param {string} endpoint
 * @param {any} data   optional payload (e.g., { ids: [...] })
 * @param {object} config
 */
export const deleteData = async (endpoint, data = undefined, config = {}) => {
  const response = await api.delete(endpoint, { data, ...config });
  return response?.data;
};

export default api;
